import { X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { AgentAvatar, isEmojiIcon } from "@/features/studio/agent-avatar"

const EMOJIS = [
  "🤖",
  "🧠",
  "✨",
  "🛠️",
  "📊",
  "📚",
  "🔍",
  "💬",
  "🧾",
  "📈",
  "🗂️",
  "🧪",
  "⚖️",
  "🛡️",
  "🚀",
  "🎯",
  "📝",
  "📅",
  "💡",
  "🧭",
  "🏗️",
  "🔧",
  "🌐",
  "📦",
]

/** Clickable agent avatar that opens an emoji picker (empty = initials). */
export function IconPicker({
  name,
  value,
  onChange,
  archived,
}: {
  name: string
  value: string
  onChange: (icon: string) => void
  archived?: boolean
}) {
  const { t } = useTranslation()
  const [custom, setCustom] = useState("")
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="focus-visible:ring-ring/50 rounded-lg transition-transform hover:scale-105 focus-visible:ring-3 focus-visible:outline-none"
            aria-label={t("studio.agents.icon.change")}
          />
        }
      >
        <AgentAvatar name={name} icon={value} size="lg" archived={archived} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <div className="text-muted-foreground text-xs font-medium">{t("studio.agents.icon.title")}</div>
        <div className="grid grid-cols-8 gap-1">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onChange(e)}
              aria-pressed={value === e}
              className="hover:bg-muted aria-pressed:bg-primary/10 aria-pressed:ring-primary/30 flex size-7 items-center justify-center rounded-md text-base aria-pressed:ring-1"
            >
              {e}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={t("studio.agents.icon.custom")}
            aria-label={t("studio.agents.icon.custom")}
            className="h-7 text-sm"
            maxLength={16}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                if (isEmojiIcon(custom.trim())) {
                  onChange(custom.trim())
                  setCustom("")
                }
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!isEmojiIcon(custom.trim())}
            onClick={() => {
              onChange(custom.trim())
              setCustom("")
            }}
          >
            {t("studio.agents.icon.use")}
          </Button>
        </div>
        {value && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="justify-start"
            onClick={() => onChange("")}
          >
            <X />
            {t("studio.agents.icon.initials")}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
